import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, REQUEST, signal } from '@angular/core';
import { User } from 'src/app/@generated';
import { Buffer } from 'buffer';

@Injectable({
    providedIn: 'root'
})
export class CookieService {
    private platformId = inject(PLATFORM_ID);
    private request = inject(REQUEST, { optional: true });

    public _token = signal<string | undefined>(this.cookies.get('token'));
    public _user = signal<User | undefined>(this.getCookieProfile());

    private getCookieProfile(): User | undefined {
        const user = this.decode(this.cookies.get('user'));

        return user ? JSON.parse(user) : undefined;
    }

    private get cookies(): Map<string, string> {
        const map = new Map<string, string>();

        let cookie = isPlatformBrowser(this.platformId) ? document.cookie : this.request?.headers.get('cookie');
        cookie?.split(';').map(c => c.trim().split('=')).forEach(([ key, value ]) => map.set(key, value));

        return map;
    }

    public get token() {
        return this._token.asReadonly();
    }

    public setToken(token: string) {
        if (token != this._token()) {
            this.set('token', token);
            this._token.set(this.cookies.get('token'));
        }
    }

    public get user() {
        return this._user.asReadonly();
    }

    public setUser(user: User) {
        if (user != this._user()) {
            this.set('user', this.encode(JSON.stringify(user))!);
            this._user.set(this.getCookieProfile());
        }
    }

    private set(key: string, value: string) {
        if (isPlatformBrowser(this.platformId)) {
            document.cookie = `${ key }=${ value }`;
        } else if (this.request) {
            console.log('Setting cookie', key, value);
            this.request.headers.getSetCookie().push(`${ key }=${ value }`);
        }
    }

    private remove(key: string) {
        if (isPlatformBrowser(this.platformId)) {
            document.cookie = `${ key }=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
        } else {
            this.set('Set-Cookie', `${ key }=; expires=Thu, 01 Jan 1970 00:00:00 UTC`);
        }
    }

    private decode(value: string | undefined): string | undefined {
        if (!value) {
            return undefined;
        }

        return Buffer.from(value, 'base64').toString();
    }

    private encode(value: string | undefined): string | undefined {
        if (!value) {
            return undefined;
        }

        return Buffer.from(value).toString('base64')
    }

    public logout() {
        this._user.set(undefined);
        this.remove('user');
        this._token.set(undefined);
        this.remove('token');
    }
}
